import type { MarketingAdCreative } from "@common/interfaces/KYDMarketing";
import { PublisherPlatforms } from "@common/interfaces/KYDMarketing";
import { normalizeCreativeIdentityForWire } from "@common/utils/guidedEditAdSerializers";
import { creativeBlockHasPartnerPost } from "@common/utils/instagramPartnershipCreative";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import type { CreativeIdentityReviewRow } from "../components/CreativeIdentityReviewRows";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  resolveCreativeBlockPartnerPlatforms,
  resolveCreativePartnerFacebookName,
  resolveCreativePartnerInstagramUsername,
  resolveMarketingPartnerFacebookDisplayLabel,
  resolveMarketingPartnerInstagramUsername,
  resolveMarketingPartnershipPlatforms,
} from "./resolveCreativePartnerDisplay";

function marketingAdCreativeHasPartnerPost(
  creative: MarketingAdCreative | undefined
): boolean {
  const partnership = creative?.partnership;
  if (!partnership) {
    return false;
  }
  return creativeBlockHasPartnerPost({
    partnerPostEnabled: partnership.enabled,
    partnerInstagramUserId: partnership.partner_instagram_user_id,
    partnerInstagramUsername: partnership.partner_instagram_username,
    partnerFacebookPageId: partnership.partner_facebook_page_id,
  });
}

export function buildCreativeBlockIdentityReviewRows(
  creative: CreativeBlock,
  pages: readonly FacebookPage[],
  instagramAccounts: readonly InstagramAccount[],
  partnerPostsEnabled = false
): CreativeIdentityReviewRow[] {
  const hasPartnerPost =
    partnerPostsEnabled && creativeBlockHasPartnerPost(creative);
  const partnerFacebookName = resolveCreativePartnerFacebookName(
    creative,
    pages
  );
  const partnerInstagramUsername = resolveCreativePartnerInstagramUsername(
    creative,
    instagramAccounts
  );
  const partnerPlatforms = resolveCreativeBlockPartnerPlatforms(creative);

  const rows: CreativeIdentityReviewRow[] = [
    {
      key: "fb-account",
      icon: FaFacebookF,
      label: "FB Account",
      value:
        typeof creative.fbPage === "object" && creative.fbPage?.name != null
          ? creative.fbPage.name
          : "—",
    },
  ];
  if (hasPartnerPost && partnerPlatforms.facebook) {
    rows.push({
      key: "fb-partner",
      icon: "link",
      label: "FB Partner",
      value: partnerFacebookName ?? "—",
    });
  }
  rows.push({
    key: "ig-account",
    icon: FaInstagram,
    label: "IG Account",
    value:
      typeof creative.instagramAccount === "object" &&
      creative.instagramAccount?.username != null
        ? creative.instagramAccount.username
        : "—",
  });
  if (hasPartnerPost && partnerPlatforms.instagram) {
    rows.push({
      key: "ig-partner",
      icon: "link",
      label: "IG Partner",
      value: partnerInstagramUsername ?? "—",
    });
  }
  return rows;
}

export type CreativeIdentityDetailDisplayData = {
  instagramAccount?: string | null;
  facebookAccount?: string | null;
  instagramPartner?: string | null;
  facebookPartner?: string | null;
};

export function creativeIdentityDetailHasContent(
  detail: CreativeIdentityDetailDisplayData
): boolean {
  return Boolean(
    detail.instagramAccount ||
      detail.facebookAccount ||
      detail.instagramPartner ||
      detail.facebookPartner
  );
}

export function buildMarketingAdCreativeIdentityDetailDisplay(
  creative: MarketingAdCreative | undefined,
  pages: readonly FacebookPage[],
  instagramAccounts: readonly InstagramAccount[],
  partnerPostsEnabled = false
): CreativeIdentityDetailDisplayData {
  const effectiveIdentity = normalizeCreativeIdentityForWire(creative?.identity);
  const facebookPageId =
    effectiveIdentity?.[PublisherPlatforms.facebook]?.page_id?.trim();
  const instagramActorId =
    effectiveIdentity?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim();
  const facebookPageName = facebookPageId
    ? pages.find((page) => page.id === facebookPageId)?.name?.trim()
    : null;
  const instagramUsername = instagramActorId
    ? instagramAccounts.find((account) => account.id === instagramActorId)
        ?.username?.trim()
    : null;
  const instagramDisplayLabel =
    instagramUsername || (instagramActorId ? "Instagram account" : null);
  const facebookDisplayLabel =
    facebookPageName || (facebookPageId ? "Facebook page" : null);

  const partnership = creative?.partnership;
  const hasPartnerPost =
    partnerPostsEnabled && marketingAdCreativeHasPartnerPost(creative);
  const partnerPlatforms = resolveMarketingPartnershipPlatforms(partnership);

  return {
    instagramAccount: instagramDisplayLabel,
    facebookAccount: facebookDisplayLabel,
    instagramPartner:
      hasPartnerPost && partnerPlatforms.instagram
        ? resolveMarketingPartnerInstagramUsername(
            partnership,
            instagramAccounts
          )
        : null,
    facebookPartner:
      hasPartnerPost && partnerPlatforms.facebook
        ? resolveMarketingPartnerFacebookDisplayLabel(partnership, pages)
        : null,
  };
}
